import React, { memo } from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Typography from '@mui/material/Typography';
import Add from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';
import { normalizeTag } from './swaggerFileModel';

interface TagInputProps {
  tags: string[];
  input: string;
  suggestions: string[];
  onTagsChange: (tags: string[]) => void;
  onInputChange: (value: string) => void;
}

const fieldLabelSx = { fontSize: 12, fontWeight: 600, color: 'text.secondary' } as const;

const suggestionSx = {
  height: 24,
  pl: 0.5,
  pr: 1,
  gap: 0.25,
  borderRadius: 1.5,
  border: 1,
  borderColor: 'divider',
  color: 'text.secondary',
  fontSize: 12,
  '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
} as const;

/** Chip-style tag editor: Enter or comma commits, Backspace on empty input removes the last tag. */
const TagInput: React.FC<TagInputProps> = ({ tags, input, suggestions, onTagsChange, onInputChange }) => {
  const add = (raw: string) => {
    const tag = normalizeTag(raw);
    if (tag && !tags.includes(tag)) onTagsChange([...tags, tag]);
    onInputChange('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      add(input);
    } else if (e.key === 'Backspace' && !input && tags.length) {
      onTagsChange(tags.slice(0, -1));
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Typography component="label" htmlFor="swagger-tag-input" sx={fieldLabelSx}>
        Tags{' '}
        <Box component="span" sx={{ fontWeight: 400, color: 'text.disabled' }}>
          optional · press Enter to add
        </Box>
      </Typography>
      <Box
        sx={{
          minHeight: 44,
          py: 0.75,
          px: 1,
          borderRadius: 2,
          border: 1,
          borderColor: 'divider',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.75,
          alignItems: 'center',
          '&:focus-within': { borderColor: 'primary.main', boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}` },
        }}
      >
        {tags.map((tag) => (
          <Box
            key={tag}
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, height: 28, pl: 1.25, pr: 0.5, borderRadius: 1.5, bgcolor: 'brand.container', color: 'brand.onContainer', fontSize: 12, fontWeight: 600 }}
          >
            {tag}
            <IconButton
              aria-label={`Remove tag ${tag}`}
              onClick={() => onTagsChange(tags.filter((t) => t !== tag))}
              sx={{ width: 20, height: 20, p: 0, color: 'inherit' }}
            >
              <Close sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        ))}
        <InputBase
          id="swagger-tag-input"
          value={input}
          onChange={(e) => onInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={tags.length ? '' : 'Add a tag'}
          sx={{ flex: 1, minWidth: 120, height: 28, fontSize: 13 }}
        />
      </Box>
      {suggestions.length ? (
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>Suggested</Typography>
          {suggestions.map((tag) => (
            <ButtonBase key={tag} onClick={() => add(tag)} sx={suggestionSx}>
              <Add sx={{ fontSize: 14 }} />
              {tag}
            </ButtonBase>
          ))}
        </Box>
      ) : null}
    </Box>
  );
};

export default memo(TagInput);
